import { useMemo } from "react";
import {
    useDefaultAccountingRules,
    useWorkspaceRuleGroups,
} from "@api/rules.api";
import {
    AccountRuleReference,
    getAccountIdsWithEnabledTransactionRules,
} from "@common/helpers/domains/accounting/accountHasEnabledTransactionRule";
import { useIsGlFirst } from "./useIsGlFirst";

interface UseAccountIdsWithEnabledRulesParams {
    accounts: AccountRuleReference[] | undefined;
    enabled: boolean;
}

interface AccountIdsWithEnabledRules {
    accountIdsWithEnabledRules: Set<string> | undefined;
    isLoading: boolean;
}

// The rules page lists the persisted groups and, outside GL-first workspaces,
// the synthetic default accounting group; "has rule" has to see the same set.
export function useAccountIdsWithEnabledRules({
    accounts,
    enabled,
}: UseAccountIdsWithEnabledRulesParams): AccountIdsWithEnabledRules {
    const { enabled: isGlFirst, initializing: isGlFirstInitializing } =
        useIsGlFirst();
    const shouldIncludeDefaultRules =
        enabled && !isGlFirstInitializing && !isGlFirst;
    const ruleGroupsQuery = useWorkspaceRuleGroups({ enabled });
    const defaultRulesQuery = useDefaultAccountingRules({
        enabled: shouldIncludeDefaultRules,
    });
    const ruleGroups = ruleGroupsQuery.data?.ruleGroups;
    const defaultRuleGroup = shouldIncludeDefaultRules
        ? defaultRulesQuery.data?.ruleGroup
        : null;

    const accountIdsWithEnabledRules = useMemo(() => {
        if (
            !accounts ||
            !ruleGroups ||
            isGlFirstInitializing ||
            defaultRuleGroup === undefined
        ) {
            return undefined;
        }
        return getAccountIdsWithEnabledTransactionRules({
            accounts,
            ruleGroups: defaultRuleGroup
                ? [...ruleGroups, defaultRuleGroup]
                : ruleGroups,
        });
    }, [accounts, ruleGroups, defaultRuleGroup, isGlFirstInitializing]);

    return {
        accountIdsWithEnabledRules,
        isLoading:
            isGlFirstInitializing ||
            ruleGroupsQuery.isLoading ||
            defaultRulesQuery.isLoading,
    };
}
